import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight, X } from "lucide-react";
import { FadeUp } from "@/components/Reveal";
import { useLang } from "@/i18n/LanguageContext";

function StackCard({ s, i, total, progress, onOpen }) {
  const { t, lang } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], [60, -60]);
  const y2 = useTransform(scrollYProgress, [0, 1], [120, -120]);
  const y3 = useTransform(scrollYProgress, [0, 1], [30, -160]);
  const step = 1 / total;
  const scale = useTransform(progress, [i * step, 1], [1, 1 - (total - i) * 0.035]);
  const dim = useTransform(progress, [i * step, Math.min(1, (i + 1.6) * step)], [0, 0.55]);
  const thumbs = [s.cover, ...s.items.map((it) => it.src).filter((src) => src !== s.cover)].slice(0, 3);
  const ys = [y1, y2, y3];

  return (
    <div ref={ref} className="h-[92vh]">
      <motion.div
        data-testid={`collection-cover-${i}`}
        role="button"
        tabIndex={0}
        onClick={() => onOpen(i)}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onOpen(i)}
        style={{ scale, top: `calc(9vh + ${i * 10}px)` }}
        className="group sticky h-[78vh] origin-top bg-[#0A0A0A] border border-[#1A1A1A] hover:border-[#75FB4E]/60 transition-colors duration-500 cursor-pointer overflow-hidden grid grid-cols-1 md:grid-cols-12"
      >
        <motion.div style={{ opacity: dim }} className="absolute inset-0 z-20 bg-[#050505] pointer-events-none" />
        <div className="md:col-span-4 flex flex-col justify-between p-6 md:p-10 border-b md:border-b-0 md:border-r border-[#1A1A1A]">
          <div className="flex items-center justify-between font-mono-tech text-[10px] tracking-[0.3em] text-[#808080] uppercase">
            <span>COLL. {String(i + 1).padStart(2, "0")}</span>
            <span>{String(s.items.length).padStart(2, "0")} {s.items.length > 1 ? t("photo.photos") : t("photo.photo")}</span>
          </div>
          <div>
            <h3 className="font-display text-4xl md:text-6xl lg:text-7xl uppercase leading-[0.9] text-[#E5E5E5] group-hover:text-[#75FB4E] transition-colors duration-300">
              {lang === "en" && s.nameEn ? s.nameEn : s.name}
            </h3>
            {s.credit && <p className="mt-4 font-mono-tech text-[10px] tracking-[0.2em] uppercase text-[#808080]">{t("photo.with")} {s.credit}</p>}
          </div>
          <span className="inline-flex items-center gap-2 font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#E5E5E5] group-hover:text-[#75FB4E] transition-colors duration-300">
            {t("photo.openCollection")} <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform duration-300" />
          </span>
        </div>
        <div className="md:col-span-8 relative grid grid-cols-3 gap-3 md:gap-4 p-3 md:p-4 overflow-hidden">
          {thumbs.map((src, k) => (
            <motion.div key={src} style={{ y: ys[k] }} className={`relative overflow-hidden ${k === 0 ? "col-span-2 row-span-2" : ""} ${k === 2 ? "hidden md:block" : ""}`}>
              <img src={src} alt={`${s.name} — ${k + 1}`} loading="lazy" className="w-full h-full object-cover scale-110 group-hover:scale-100 transition-transform duration-700 ease-out" />
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  );
}

const series = [
  {
    name: "Série Rouge",
    nameEn: "Red Series",
    theme: "couleur",
    cover: "/images/photo-fenetre-rouge.webp",
    items: [
      { src: "/images/photo-arbre-rouge.webp", title: "Canopée", titleEn: "Canopy", meta: "FILTRE ROUGE — NUMÉRIQUE" },
      { src: "/images/photo-echelle.webp", title: "L'Échelle", titleEn: "The Ladder", meta: "LUMIÈRE ROUGE — NUMÉRIQUE" },
      { src: "/images/photo-fenetre-rouge.webp", title: "Fenêtre Rouge", titleEn: "Red Window", meta: "FILTRE ROUGE — NUMÉRIQUE" },
      { src: "/images/photo-cadre-rouge.webp", title: "Cadre Rouge", titleEn: "Red Frame", meta: "FILTRE ROUGE — NUMÉRIQUE" },
      { src: "/images/photo-chat-rouge.webp", title: "Le Chat", titleEn: "The Cat", meta: "LUMIÈRE ROUGE — NUMÉRIQUE" },
      { src: "/images/photo-portrait-rouge.webp", title: "Crépuscule", titleEn: "Dusk", meta: "LUMIÈRE ROUGE — NUMÉRIQUE" },
      { src: "/images/photo-lumieres-rouges.webp", title: "Constellation", titleEn: "Constellation", meta: "LUMIÈRE ROUGE — NUMÉRIQUE" },
      { src: "/images/photo-portrait-nb.webp", title: "Face", titleEn: "Face", meta: "N&B — NUMÉRIQUE" },
      { src: "/images/photo-branches-rouges.webp", title: "Ramure", titleEn: "Branches", meta: "LUMIÈRE ROUGE — NUMÉRIQUE" },
      { src: "/images/photo-visage-flou.webp", title: "Flou", titleEn: "Blur", meta: "N&B — NUMÉRIQUE" },
      { src: "/images/photo-main-rouge.webp", title: "La Main", titleEn: "The Hand", meta: "LUMIÈRE ROUGE — NUMÉRIQUE" },
    ],
    credit: "Esteban Rigallaud-Leal",
  },
  {
    name: "Corée",
    nameEn: "Korea",
    theme: "voyage",
    cover: "/images/photo-marche-seoul.webp",
    items: [
      { src: "/images/photo-marche-seoul.webp", title: "Marché de Busan", titleEn: "Busan Market", meta: "BUSAN — NUMÉRIQUE" },
      { src: "/images/photo-gwangjang.webp", title: "Gwangjang de Nuit", titleEn: "Gwangjang by Night", meta: "SÉOUL — NUMÉRIQUE" },
      { src: "/images/photo-poissonniere.webp", title: "La Poissonnière", titleEn: "The Fishmonger", meta: "BUSAN — NUMÉRIQUE" },
      { src: "/images/photo-poissonnerie-busan.webp", title: "Marché de Jagalchi", titleEn: "Jagalchi Market", meta: "BUSAN — NUMÉRIQUE" },
      { src: "/images/photo-brocante-seoul.webp", title: "La Brocante", titleEn: "The Flea Market", meta: "SÉOUL — NUMÉRIQUE" },
      { src: "/images/photo-scooters-neige.webp", title: "La Neige de Séoul", titleEn: "Snow in Seoul", meta: "SÉOUL — NUMÉRIQUE" },
    ],
  },
  {
    name: "Inde",
    nameEn: "India",
    theme: "voyage",
    cover: "/images/photo-inde-2.webp",
    items: [
      { src: "/images/photo-inde-1.webp", title: "Grottes de Kanheri", titleEn: "Kanheri Caves", meta: "MUMBAÏ — NUMÉRIQUE" },
      { src: "/images/photo-inde-2.webp", title: "Rickshaw", titleEn: "Rickshaw", meta: "MUMBAÏ — NUMÉRIQUE" },
      { src: "/images/photo-inde-3.webp", title: "Architecture de Mumbaï", titleEn: "Mumbai Architecture", meta: "MUMBAÏ — NUMÉRIQUE" },
      { src: "/images/photo-inde-4.webp", title: "L'Abri", titleEn: "The Shelter", meta: "MUMBAÏ — NUMÉRIQUE" },
      { src: "/images/photo-inde-5.webp", title: "Skyline au Couchant", titleEn: "Skyline at Dusk", meta: "MUMBAÏ — NUMÉRIQUE" },
    ],
  },
  {
    name: "Portraits",
    nameEn: "Portraits",
    theme: "portrait",
    cover: "/images/photo-portrait-femme.webp",
    items: [
      { src: "/images/photo-portrait-femme.webp", title: "Regard", titleEn: "Gaze", meta: "STUDIO — COULEUR" },
      { src: "/images/photo-portrait-homme.webp", title: "Face à Face", titleEn: "Face to Face", meta: "RUE — N&B" },
      { src: "/images/photo-instant-regard-laine.webp", title: "Laine", titleEn: "Wool", meta: "PORTRAIT — N&B" },
      { src: "/images/photo-instant-gouttes.webp", title: "Complicité", titleEn: "Closeness", meta: "PORTRAIT — NUMÉRIQUE" },
      { src: "/images/photo-instant-spa.webp", title: "L'Attente", titleEn: "The Wait", meta: "RUE — NUMÉRIQUE" },
    ],
  },
  {
    name: "Silhouettes",
    nameEn: "Silhouettes",
    theme: "nb",
    cover: "/images/photo-silhouettes-1.webp",
    items: [
      { src: "/images/photo-silhouettes-1.webp", title: "Silhouettes I", titleEn: "Silhouettes I", meta: "BUSAN — N&B" },
      { src: "/images/photo-silhouettes-2.webp", title: "Silhouettes II", titleEn: "Silhouettes II", meta: "BUSAN — N&B" },
      { src: "/images/photo-silhouettes-3.webp", title: "Silhouettes III", titleEn: "Silhouettes III", meta: "BUSAN — N&B" },
    ],
  },
  {
    name: "Mademoiselle",
    nameEn: "Mademoiselle",
    theme: "studio",
    cover: "/images/photo-rochas.webp",
    items: [
      { src: "/images/photo-rochas.webp", title: "Mademoiselle", titleEn: "Mademoiselle", meta: "STUDIO — NUMÉRIQUE" },
      { src: "/images/photo-rochas-2.webp", title: "Vanité", titleEn: "Vanity", meta: "STUDIO — NATURE MORTE" },
      { src: "/images/photo-rochas-3.webp", title: "Ombres Fleuries", titleEn: "Flowered Shadows", meta: "STUDIO — NUMÉRIQUE" },
    ],
  },
  {
    name: "Beauty of Joseon",
    nameEn: "Beauty of Joseon",
    theme: "studio",
    cover: "/images/beauty-of-joseon/photo-set-design.webp",
    credit: "Sire Lola",
    desc: "Campagne publicitaire pour la marque coréenne Beauty of Joseon (2026) : direction artistique produit et photographie. Une lumière douce et botanique pour une beauté qui puise dans la tradition.",
    descEn: "Advertising campaign for the Korean brand Beauty of Joseon (2026): product art direction and photography. A soft, botanical light for a beauty rooted in tradition.",
    items: [
      { src: "/images/beauty-of-joseon/photo-set-design.webp", title: "Set Design I", titleEn: "Set Design I", meta: "CAMPAGNE — STUDIO" },
      { src: "/images/beauty-of-joseon/photo-set-design-2.webp", title: "Set Design II", titleEn: "Set Design II", meta: "CAMPAGNE — STUDIO" },
      { src: "/images/beauty-of-joseon/photo-eshop.webp", title: "E-shop", titleEn: "E-shop", meta: "PACKSHOT — STUDIO" },
      { src: "/images/beauty-of-joseon/photo-lifestyle.webp", title: "Lifestyle", titleEn: "Lifestyle", meta: "CAMPAGNE — MODÈLE" },
      { src: "/images/beauty-of-joseon/photo-texture.webp", title: "Texture", titleEn: "Texture", meta: "MACRO — STUDIO" },
      { src: "/images/beauty-of-joseon/photo-artistique.webp", title: "Artistique I", titleEn: "Artistic I", meta: "CAMPAGNE — STUDIO" },
      { src: "/images/beauty-of-joseon/photo-artistique-2.webp", title: "Artistique II", titleEn: "Artistic II", meta: "CAMPAGNE — STUDIO" },
      { src: "/images/beauty-of-joseon.webp", title: "Campagne", titleEn: "Campaign", meta: "CAMPAGNE PUBLICITAIRE" },
    ],
  },
  {
    name: "Tinajera",
    nameEn: "Tinajera",
    theme: "studio",
    cover: "/images/photo-ceramique-1.webp",
    desc: "Tinajera, un atelier de céramique : la terre tournée, façonnée et émaillée à la main, photographiée en nature morte.",
    descEn: "Tinajera, a ceramics workshop: clay thrown, shaped and glazed by hand, captured in still life.",
    items: [
      { src: "/images/photo-ceramique-1.webp", title: "Bols", titleEn: "Bowls", meta: "NATURE MORTE — NUMÉRIQUE" },
      { src: "/images/photo-ceramique-2.webp", title: "Gypsophile", titleEn: "Gypsophila", meta: "NATURE MORTE — NUMÉRIQUE" },
      { src: "/images/photo-instant-banc.webp", title: "Argile", titleEn: "Clay", meta: "ATELIER — NUMÉRIQUE" },
    ],
  },
  {
    name: "Autres",
    nameEn: "Others",
    hidden: true,
    theme: "studio",
    cover: "/images/photo-chloe-parfum.webp",
    items: [
      { src: "/images/photo-chloe-parfum.webp", title: "Chloé à Fleur d'Eau", titleEn: "Water Bloom", meta: "RETOUCHE — COMPOSITING", theme: "studio" },
      { src: "/images/photo-instant-duo.webp", title: "Tête-Bêche", titleEn: "Head to Toe", meta: "PORTRAIT — NUMÉRIQUE", theme: "portrait" },
    ],
  },
];

const visibleSeries = series.filter((s) => !s.hidden);

const themes = [
  { id: "all", fr: "Tout", en: "All" },
  { id: "voyage", fr: "Voyage", en: "Travel" },
  { id: "studio", fr: "Studio", en: "Studio" },
  { id: "portrait", fr: "Portrait", en: "Portrait" },
  { id: "couleur", fr: "Couleur", en: "Colour" },
  { id: "nb", fr: "N&B", en: "B&W" },
];

const shuffle = (arr) => {
  const a = [...arr];
  let seed = 7;
  for (let i = a.length - 1; i > 0; i--) {
    seed = (seed * 9301 + 49297) % 233280;
    const j = Math.floor((seed / 233280) * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const allPhotos = shuffle(series.flatMap((s, si) => s.items.map((it, pi) => ({ ...it, s: si, p: pi, collection: s.name, collectionEn: s.nameEn, theme: it.theme || s.theme }))));

export default function Photo({ lenisRef }) {
  const { t, lang } = useLang();
  const [openSeries, setOpenSeries] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    const onOpen = (e) => {
      const { s, p, name } = e.detail || {};
      const byName = name ? series.findIndex((c) => c.name === name) : -1;
      setOpenSeries(byName >= 0 ? byName : typeof s === "number" ? s : 0);
      setLightbox(typeof p === "number" ? p : null);
    };
    window.addEventListener("open-photo", onOpen);
    return () => window.removeEventListener("open-photo", onOpen);
  }, []);

  useEffect(() => {
    if (openSeries !== null) {
      lenisRef?.current?.stop();
    } else {
      lenisRef?.current?.start();
    }
  }, [openSeries, lenisRef]);

  const current = openSeries !== null ? series[openSeries] : null;

  useEffect(() => {
    const onKey = (e) => {
      if (openSeries === null) return;
      if (e.key === "Escape") {
        if (lightbox !== null) setLightbox(null);
        else setOpenSeries(null);
      }
      if (lightbox !== null && current) {
        if (e.key === "ArrowRight") setLightbox((v) => Math.min(v + 1, current.items.length - 1));
        if (e.key === "ArrowLeft") setLightbox((v) => Math.max(v - 1, 0));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openSeries, lightbox, current]);

  const openCol = (i) => {
    setOpenSeries(i);
    setLightbox(null);
  };

  const stackRef = useRef(null);
  const { scrollYProgress: stackProgress } = useScroll({ target: stackRef, offset: ["start start", "end end"] });
  const [view, setView] = useState("mix");
  const [theme, setTheme] = useState("all");
  const MIX_STEP = 12;
  const [mixLimit, setMixLimit] = useState(MIX_STEP);

  const filteredPhotos = theme === "all" ? allPhotos : allPhotos.filter((ph) => ph.theme === theme);
  const filteredSeries = series.map((s, i) => ({ s, i })).filter(({ s }) => !s.hidden && (theme === "all" || s.theme === theme));
  const visiblePhotos = filteredPhotos.slice(0, mixLimit);
  const hasMore = mixLimit < filteredPhotos.length;

  const nextVisibleIndex = (from) => {
    for (let k = 1; k <= series.length; k++) {
      const idx = (from + k) % series.length;
      if (!series[idx].hidden) return idx;
    }
    return from;
  };

  const selectTheme = (id) => {
    setTheme(id);
    setMixLimit(MIX_STEP);
  };

  return (
    <section id="photo" data-testid="photo-section" className="px-6 md:px-12 py-28 md:py-40 border-t border-[#1A1A1A]">
      <FadeUp className="flex items-baseline gap-4 mb-12 md:mb-16">
        <span className="font-mono-tech text-sm text-[#75FB4E]">(03)</span>
        <h2 className="font-display text-5xl md:text-8xl uppercase leading-none">{t("photo.section")}</h2>
        <span className="font-mono-tech text-xs text-[#808080] ml-auto hidden md:inline">
          {t("photo.subtitle", { n: String(visibleSeries.length).padStart(2, "0") })}
        </span>
      </FadeUp>

      <div className="flex flex-wrap items-center gap-3 mb-6" data-testid="photo-view-toggle">
        {[
          { id: "mix", label: `${t("photo.allPhotos")} (${allPhotos.length})` },
          { id: "collections", label: `${t("photo.byCollection")} (${visibleSeries.length})` },
        ].map((v) => (
          <button
            key={v.id}
            data-testid={`photo-view-${v.id}`}
            onClick={() => setView(v.id)}
            className={`font-mono-tech text-[10px] tracking-[0.3em] uppercase px-5 py-3 border transition-colors duration-300 ${
              view === v.id ? "border-[#75FB4E] text-[#75FB4E]" : "border-[#2A2A2A] text-[#808080] hover:text-[#E5E5E5] hover:border-[#E5E5E5]"
            }`}
          >
            {v.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-10" data-testid="photo-theme-filter">
        {themes.map((th) => (
          <button
            key={th.id}
            data-testid={`photo-theme-${th.id}`}
            onClick={() => selectTheme(th.id)}
            className={`font-mono-tech text-[10px] tracking-[0.25em] uppercase px-4 py-2 rounded-full border transition-colors duration-300 ${
              theme === th.id ? "border-[#75FB4E] text-[#75FB4E] bg-[#75FB4E]/5" : "border-[#2A2A2A] text-[#808080] hover:text-[#E5E5E5] hover:border-[#E5E5E5]"
            }`}
          >
            {lang === "en" ? th.en : th.fr}
          </button>
        ))}
      </div>

      {view === "mix" ? (
        <>
        <div data-testid="photo-mix" className="columns-2 md:columns-3 lg:columns-4 gap-4 [column-fill:_balance]">
          {visiblePhotos.map((ph, idx) => (
            <motion.button
              key={`${ph.src}-${idx}`}
              data-testid={`mix-photo-${idx}`}
              onClick={() => {
                setOpenSeries(ph.s);
                setLightbox(ph.p);
              }}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: (idx % 4) * 0.06 }}
              className="group relative block w-full mb-4 overflow-hidden border border-[#1A1A1A] hover:border-[#75FB4E]/60 transition-colors duration-500 break-inside-avoid"
            >
              <img src={ph.src} alt={`${ph.title} — ${ph.collection}`} loading="lazy" className="w-full h-auto group-hover:scale-[1.03] transition-transform duration-700 ease-out" />
              <span className="absolute inset-x-0 bottom-0 flex items-center justify-between px-3 py-2 bg-[#050505]/80 backdrop-blur-sm font-mono-tech text-[9px] tracking-[0.2em] uppercase text-[#E5E5E5] translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                <span className="truncate">{lang === "en" && ph.titleEn ? ph.titleEn : ph.title}</span>
                <span className="text-[#75FB4E] whitespace-nowrap ml-2">{lang === "en" && ph.collectionEn ? ph.collectionEn : ph.collection}</span>
              </span>
            </motion.button>
          ))}
        </div>
        <div className="mt-12 flex justify-center">
          {hasMore ? (
            <button
              data-testid="photo-mix-load-more"
              onClick={() => setMixLimit((v) => Math.min(v + MIX_STEP, filteredPhotos.length))}
              className="group inline-flex items-center gap-3 border border-[#2A2A2A] px-8 py-4 font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#E5E5E5] hover:border-[#75FB4E] hover:text-[#75FB4E] transition-colors duration-300"
            >
              {t("photo.viewMore")}
              <span className="text-[#808080] group-hover:text-[#75FB4E] transition-colors duration-300">
                ({visiblePhotos.length}/{filteredPhotos.length})
              </span>
              <ArrowRight className="w-4 h-4 rotate-90 group-hover:translate-y-1 transition-transform duration-300" />
            </button>
          ) : (
            mixLimit > MIX_STEP && (
              <button
                data-testid="photo-mix-show-less"
                onClick={() => {
                  setMixLimit(MIX_STEP);
                  document.getElementById("photo")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="group inline-flex items-center gap-3 border border-[#2A2A2A] px-8 py-4 font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#E5E5E5] hover:border-[#75FB4E] hover:text-[#75FB4E] transition-colors duration-300"
              >
                {t("photo.viewLess")}
                <ArrowRight className="w-4 h-4 -rotate-90 group-hover:-translate-y-1 transition-transform duration-300" />
              </button>
            )
          )}
        </div>
        </>
      ) : (
      <div ref={stackRef} data-testid="photo-stack" className="relative">
        {filteredSeries.map(({ s, i }) => (
          <StackCard key={s.name} s={s} i={i} total={series.length} progress={stackProgress} onOpen={openCol} />
        ))}
      </div>
      )}

      <AnimatePresence>
        {current && (
          <motion.div
            data-testid="collection-overlay"
            data-lenis-prevent
            className="fixed inset-0 z-[105] bg-[#050505] overflow-y-auto"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="sticky top-0 z-10 flex items-center justify-between px-6 md:px-12 py-5 bg-[#050505]/90 backdrop-blur-md border-b border-[#1A1A1A]">
              <span className="font-mono-tech text-xs tracking-[0.3em] text-[#808080] uppercase">
                (03) {t("photo.section")} — <span className="text-[#75FB4E]">{lang === "en" && current.nameEn ? current.nameEn : current.name}</span>
              </span>
              <button
                data-testid="collection-close"
                onClick={() => {
                  setOpenSeries(null);
                  setLightbox(null);
                }}
                className="group inline-flex items-center gap-2 font-mono-tech text-xs tracking-[0.3em] text-[#E5E5E5] hover:text-[#75FB4E] transition-colors duration-300"
              >
                {t("pp.close")} <X className="w-4 h-4" />
              </button>
            </div>

            <div className="px-6 md:px-12 py-16 max-w-7xl mx-auto">
              <h3 className="font-display text-4xl md:text-7xl uppercase leading-none mb-4">{lang === "en" && current.nameEn ? current.nameEn : current.name}</h3>
              <p className="font-mono-tech text-xs tracking-[0.3em] text-[#808080] uppercase mb-3">
                {String(current.items.length).padStart(2, "0")} {current.items.length > 1 ? t("photo.photos") : t("photo.photo")} — {t("photo.clickEnlarge")}
              </p>
              {(current.desc || current.descEn) && <p data-testid="collection-desc" className="max-w-2xl text-sm md:text-base font-light leading-relaxed text-[#808080] mb-4">{lang === "en" && current.descEn ? current.descEn : current.desc}</p>}
              {current.credit && (
                <p data-testid="collection-credit" className="font-mono-tech text-xs tracking-[0.2em] uppercase mb-12">
                  <span className="text-[#808080]">{t("photo.collab")} </span>
                  <span className="text-[#75FB4E]">{current.credit}</span>
                </p>
              )}
              <div className={`grid grid-cols-1 gap-6 items-start ${current.credit ? "" : "mt-12"} ${current.items.length > 1 ? "md:grid-cols-2" : "max-w-3xl"}`}>
                {current.items.map((it, idx) => (
                  <button
                    key={it.src}
                    data-testid={`collection-photo-${idx}`}
                    onClick={() => setLightbox(idx)}
                    className="group relative block overflow-hidden border border-[#1A1A1A] hover:border-[#75FB4E]/50 transition-colors duration-500"
                  >
                    <img
                      src={it.src}
                      alt={`${it.title} — ${current.name}`}
                      loading="lazy"
                      className="w-full h-auto group-hover:scale-[1.02] transition-transform duration-700 ease-out"
                    />
                    <span className="absolute bottom-3 left-3 font-mono-tech text-[10px] tracking-widest bg-[#050505]/80 px-2 py-1 text-[#808080] group-hover:text-[#75FB4E] transition-colors duration-300 uppercase">
                      ({String(idx + 1).padStart(2, "0")}) {lang === "en" && it.titleEn ? it.titleEn : it.title}
                    </span>
                  </button>
                ))}
              </div>
              <div className="mt-16 text-center pb-8">
                <p className="font-mono-tech text-[10px] tracking-[0.3em] text-[#808080] uppercase mb-4">{t("photo.nextCollection")}</p>
                <button
                  data-testid="next-collection-button"
                  onClick={() => openCol(nextVisibleIndex(openSeries))}
                  className="font-display text-3xl md:text-5xl uppercase text-outline"
                >
                  {(() => { const n = series[nextVisibleIndex(openSeries)]; return lang === "en" && n.nameEn ? n.nameEn : n.name; })()}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {current && lightbox !== null && (
          <motion.div
            data-testid="photo-lightbox"
            className="fixed inset-0 z-[120] bg-[#050505] flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex items-center justify-between px-6 md:px-12 py-5">
              <span data-testid="photo-lightbox-counter" className="font-mono-tech text-xs tracking-[0.3em] text-[#808080] uppercase">
                {lang === "en" && current.nameEn ? current.nameEn : current.name} — {String(lightbox + 1).padStart(2, "0")} / {current.items.length}
              </span>
              <button
                data-testid="photo-lightbox-close"
                onClick={() => setLightbox(null)}
                className="group inline-flex items-center gap-2 font-mono-tech text-xs tracking-[0.3em] text-[#E5E5E5] hover:text-[#75FB4E] transition-colors duration-300"
              >
                {t("pp.close")} <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 flex items-center justify-center px-6 md:px-24 pb-6 min-h-0">
              <motion.img
                key={lightbox}
                src={current.items[lightbox].src}
                alt={`${current.items[lightbox].title}`}
                data-testid="photo-lightbox-image"
                className="max-w-full max-h-full object-contain"
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              />
            </div>
            <div className="flex items-center justify-center gap-6 pb-8">
              <button
                data-testid="photo-lightbox-prev"
                onClick={() => setLightbox((v) => Math.max(v - 1, 0))}
                disabled={lightbox === 0}
                className="group inline-flex items-center gap-2 border border-[#2A2A2A] px-6 py-3 font-mono-tech text-[10px] tracking-[0.3em] text-[#E5E5E5] hover:border-[#75FB4E] hover:text-[#75FB4E] transition-colors duration-300 disabled:opacity-30 disabled:pointer-events-none"
              >
                <ArrowLeft className="w-4 h-4" /> {t("pp.prev")}
              </button>
              <button
                data-testid="photo-lightbox-next"
                onClick={() => setLightbox((v) => Math.min(v + 1, current.items.length - 1))}
                disabled={lightbox === current.items.length - 1}
                className="group inline-flex items-center gap-2 border border-[#2A2A2A] px-6 py-3 font-mono-tech text-[10px] tracking-[0.3em] text-[#E5E5E5] hover:border-[#75FB4E] hover:text-[#75FB4E] transition-colors duration-300 disabled:opacity-30 disabled:pointer-events-none"
              >
                {t("pp.next")} <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
